import {
  isLoanStatus,
  LOAN_STATUS_LABELS,
  LOAN_STATUSES,
  type LoanStatus,
} from '../enums/loan-status.enum.js'
import { isOneOf } from '../helpers/is-one-of.js'

/**
 * Validated loan status graph.
 *
 * Happy path (linear):
 *   Incomplete → Underwriting → Pre-Approved → Approved →
 *   Clear to Close → Clear to Close - Fund → Originated
 *
 * Exception / terminal exits branch from intermediate stages (no reverse).
 * Refine edges in this file only — Prisma enum + lib must stay in sync.
 */
export const LOAN_STATUS_TRANSITIONS: Record<
  LoanStatus,
  readonly LoanStatus[]
> = {
  INCOMPLETE: [
    'UNDERWRITING',
    'WITHDRAWN',
    'CLOSED_FOR_INCOMPLETE',
    'DELETED',
  ],
  UNDERWRITING: [
    'PRE_APPROVED',
    'DENIED',
    'WITHDRAWN',
    'CLOSED_FOR_INCOMPLETE',
  ],
  PRE_APPROVED: [
    'APPROVED',
    'PRE_APPROVED_NOT_ACCEPTED',
    'DENIED_PRE_APPROVAL',
    'WITHDRAWN',
  ],
  APPROVED: [
    'CLEAR_TO_CLOSE',
    'APPROVED_NOT_ACCEPTED',
    'WITHDRAWN',
    'DENIED',
  ],
  CLEAR_TO_CLOSE: ['CLEAR_TO_CLOSE_FUND', 'WITHDRAWN', 'DENIED'],
  CLEAR_TO_CLOSE_FUND: ['ORIGINATED', 'WITHDRAWN'],
  ORIGINATED: [],
  APPROVED_NOT_ACCEPTED: [],
  PRE_APPROVED_NOT_ACCEPTED: [],
  DENIED_PRE_APPROVAL: [],
  WITHDRAWN: [],
  DENIED: [],
  CLOSED_FOR_INCOMPLETE: [],
  DELETED: [],
} as const

/** Initial status for newly created loans. */
export const LOAN_INITIAL_STATUS: LoanStatus = 'INCOMPLETE'

export const LOAN_TERMINAL_STATUSES: readonly LoanStatus[] = [
  'ORIGINATED',
  'APPROVED_NOT_ACCEPTED',
  'PRE_APPROVED_NOT_ACCEPTED',
  'DENIED_PRE_APPROVAL',
  'WITHDRAWN',
  'DENIED',
  'CLOSED_FOR_INCOMPLETE',
  'DELETED',
] as const

/** Ordered happy-path pipeline (excludes terminal exits). */
export const LOAN_HAPPY_PATH: readonly LoanStatus[] = [
  'INCOMPLETE',
  'UNDERWRITING',
  'PRE_APPROVED',
  'APPROVED',
  'CLEAR_TO_CLOSE',
  'CLEAR_TO_CLOSE_FUND',
  'ORIGINATED',
] as const

export function isLoanTerminalStatus(status: LoanStatus): boolean {
  return isOneOf(status, LOAN_TERMINAL_STATUSES)
}

export function allowedLoanStatusTransitions(
  from: LoanStatus,
): readonly LoanStatus[] {
  return LOAN_STATUS_TRANSITIONS[from]
}

export function canTransitionLoanStatus(
  from: LoanStatus,
  to: LoanStatus,
): boolean {
  if (from === to) return false
  return LOAN_STATUS_TRANSITIONS[from].includes(to)
}

export type LoanStatusTransitionErrorCode =
  | 'UNKNOWN_STATUS'
  | 'SAME_STATUS'
  | 'TERMINAL'
  | 'INVALID_TRANSITION'

export type LoanStatusTransitionResult =
  | { ok: true }
  | {
      ok: false
      code: LoanStatusTransitionErrorCode
      message: string
    }

/**
 * Validate a status change. Callers map failures to HTTP 400.
 */
export function validateLoanStatusTransition(
  from: string,
  to: string,
): LoanStatusTransitionResult {
  if (!isLoanStatus(from) || !isLoanStatus(to)) {
    return {
      ok: false,
      code: 'UNKNOWN_STATUS',
      message: `Unknown loan status (allowed: ${LOAN_STATUSES.map((s) => LOAN_STATUS_LABELS[s]).join(', ')})`,
    }
  }
  if (from === to) {
    return {
      ok: false,
      code: 'SAME_STATUS',
      message: `Loan is already ${LOAN_STATUS_LABELS[from]}`,
    }
  }
  if (isLoanTerminalStatus(from)) {
    return {
      ok: false,
      code: 'TERMINAL',
      message: `Loan status ${LOAN_STATUS_LABELS[from]} is terminal; no further transitions are allowed`,
    }
  }
  if (!canTransitionLoanStatus(from, to)) {
    const allowed = LOAN_STATUS_TRANSITIONS[from]
    return {
      ok: false,
      code: 'INVALID_TRANSITION',
      message:
        allowed.length === 0
          ? `Cannot transition from ${LOAN_STATUS_LABELS[from]}`
          : `Invalid transition ${LOAN_STATUS_LABELS[from]} → ${LOAN_STATUS_LABELS[to]}. Allowed: ${allowed.map((s) => LOAN_STATUS_LABELS[s]).join(', ')}`,
    }
  }
  return { ok: true }
}
